import { useCallback, useEffect, useState } from 'react'

const THEME_KEY = 'billing_portal_theme'

function readStoredTheme(): string | null {
  try {
    return localStorage.getItem(THEME_KEY)
  } catch {
    return null
  }
}

function applyTheme(dark: boolean) {
  document.documentElement.classList.toggle('dark', dark)
}

export function useTheme() {
  // An explicit toggle is remembered in localStorage (a recorded deviation —
  // the prototype has no persistence). With nothing stored the portal boots
  // light, like the prototype's `<html data-theme="light">`; the OS
  // `prefers-color-scheme` setting is deliberately not consulted (PO
  // decision 2026-10-07, MS-818 OQ-12; ARCHITECTURE_DECISIONS.md §40).
  const [isDark, setIsDark] = useState(() => readStoredTheme() === 'dark')

  useEffect(() => {
    applyTheme(isDark)
  }, [isDark])

  const toggle = useCallback(() => {
    setIsDark((prev) => {
      const next = !prev
      try {
        localStorage.setItem(THEME_KEY, next ? 'dark' : 'light')
      } catch {
        // Storage unavailable (private window, blocked site data): the toggle
        // still applies for this page view, it just isn't remembered.
      }
      return next
    })
  }, [])

  return { isDark, toggle }
}
